import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { fetchData } from '../../services/dataApi';

function useCommonFoundationsData(langCode) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [usedLang, setUsedLang] = useState(langCode);
  const [error, setError]       = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    fetchData(`common-foundations/${langCode}/common-foundations.json`)
      .then((d) => { setData(d); setUsedLang(d.contentLanguage || langCode); setLoading(false); })
      .catch(() =>
        fetchData('common-foundations/en/common-foundations.json')
          .then((d) => { setData(d); setUsedLang('en'); setLoading(false); })
          .catch(() => { setError(true); setLoading(false); })
      );
  }, [langCode]);

  return { data, loading, usedLang, error };
}

function Skeleton() {
  return (
    <div className="space-y-8 animate-pulse">
      {[1, 2, 3].map((i) => (
        <div key={i} className="rounded-2xl bg-white p-6 shadow-sm space-y-3">
          <div className="h-6 w-1/3 rounded bg-slate-200" />
          <div className="h-4 w-full rounded bg-slate-100" />
          <div className="h-4 w-5/6 rounded bg-slate-100" />
        </div>
      ))}
    </div>
  );
}

export default function CommonFoundationsPage() {
  const { t } = useTranslation();
  const { languagePrefs } = useAuth();
  const [searchParams] = useSearchParams();
  const requestedLanguage = searchParams.get('nativeLanguage');
  const deepLinkLanguage = requestedLanguage && /^[a-z]{2,3}$/i.test(requestedLanguage)
    ? requestedLanguage.toLowerCase()
    : null;
  const langCode = deepLinkLanguage || languagePrefs?.nativeLanguage || 'en';
  const { data, loading, usedLang, error } = useCommonFoundationsData(langCode);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 px-4 py-8">
      <div className="mx-auto max-w-5xl space-y-8">

        {/* Header */}
        <section className="rounded-2xl bg-white p-6 shadow-sm">
          {error ? (
            <p className="text-slate-600">{t('commonFoundations.loadError')}</p>
          ) : loading || !data ? (
            <div className="animate-pulse space-y-3">
              <div className="h-8 w-2/3 rounded bg-slate-200" />
              <div className="h-4 w-full rounded bg-slate-100" />
            </div>
          ) : (
            <>
              <h1 className="text-3xl font-bold text-slate-800">{data.title}</h1>
              <p className="mt-3 text-slate-600">{data.subtitle}</p>
              {usedLang !== langCode && (
                <p className="mt-2 text-xs font-semibold text-amber-600">
                  {t('commonFoundations.fallbackNotice')}
                </p>
              )}
            </>
          )}
        </section>

        {!error && (loading || !data ? <Skeleton /> : (
          <>
            {/* Development Stages */}
            <section className="rounded-2xl bg-white p-6 shadow-sm">
              <h2 className="text-2xl font-semibold text-slate-800">{data.developmentStages.title}</h2>
              <div className="mt-5 space-y-4">
                {data.developmentStages.items.map((item) => (
                  <article key={item.stage} className="rounded-xl border border-slate-200 overflow-hidden">
                    {item.image && (
                      <img
                        src={item.image}
                        alt={item.stage}
                        className="w-full object-contain"
                      />
                    )}
                    <div className="p-4">
                      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                        <h3 className="text-lg font-semibold text-slate-800">{item.stage}</h3>
                        <span className="text-sm font-medium text-blue-700">{item.age}</span>
                      </div>
                      <ul className="mt-3 list-disc space-y-1 pl-5 text-slate-700">
                        {item.points.map((point, i) => <li key={i}>{point}</li>)}
                      </ul>
                    </div>
                  </article>
                ))}
              </div>
            </section>

            {/* Phonetics */}
            <section className="rounded-2xl bg-white p-6 shadow-sm">
              <h2 className="text-2xl font-semibold text-slate-800">{data.phonetics.title}</h2>
              <p className="mt-3 text-slate-700">{data.phonetics.description}</p>

              <div className="mt-5 grid gap-4 md:grid-cols-2">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                  <h3 className="text-lg font-semibold text-emerald-800">{data.phonetics.vowels.title}</h3>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-emerald-900">
                    {data.phonetics.vowels.points.map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                </div>
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <h3 className="text-lg font-semibold text-amber-800">{data.phonetics.consonants.title}</h3>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-amber-900">
                    {data.phonetics.consonants.points.map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                </div>
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-2">
                <article className="rounded-xl border border-slate-200 p-4">
                  <h3 className="text-lg font-semibold text-slate-800">{data.phonetics.vowelExamples.title}</h3>
                  <ul className="mt-3 space-y-2 text-slate-700">
                    {data.phonetics.vowelExamples.items.map((item) => (
                      <li key={item.letter}>
                        <span className="font-semibold">{item.letter}</span> — {item.sound} — {item.note}
                      </li>
                    ))}
                  </ul>
                </article>
                <article className="rounded-xl border border-slate-200 p-4">
                  <h3 className="text-lg font-semibold text-slate-800">{data.phonetics.consonantExamples.title}</h3>
                  <ul className="mt-3 space-y-2 text-slate-700">
                    {data.phonetics.consonantExamples.items.map((item) => (
                      <li key={item.letter}>
                        <span className="font-semibold">{item.letter}</span>
                        {item.sound ? ` — ${item.sound}` : ''} — {item.note}
                      </li>
                    ))}
                  </ul>
                </article>
              </div>

              <article className="mt-6 rounded-xl border border-slate-200 p-4">
                <h3 className="text-lg font-semibold text-slate-800">{data.phonetics.syllables.title}</h3>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-slate-700">
                  {data.phonetics.syllables.points.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </article>
            </section>
          </>
        ))}
      </div>
    </div>
  );
}
